import { ARCADE_THEME } from '../arcadeTheme';

/** Stackfall playfield (internal key: tetris) — matches backend agent grid (10×20). */
export const TETRIS_BOARD_WIDTH = 10;
export const TETRIS_BOARD_HEIGHT = 20;

/** Classic web cell size (30px) scaled 1.2× for a slightly larger playfield. */
export const TETRIS_BASE_CELL_SIZE = 30;
export const TETRIS_DISPLAY_SCALE = 1.2;
export const TETRIS_CELL_SIZE = Math.round(TETRIS_BASE_CELL_SIZE * TETRIS_DISPLAY_SCALE);
export const TETRIS_CELL_WIDTH = TETRIS_CELL_SIZE;
export const TETRIS_CELL_HEIGHT = TETRIS_CELL_SIZE;

export const TETRIS_CANVAS_W = TETRIS_BOARD_WIDTH * TETRIS_CELL_SIZE;
export const TETRIS_CANVAS_H = TETRIS_BOARD_HEIGHT * TETRIS_CELL_SIZE;

export const TETRIS_PREVIEW_COLS = 4;
export const TETRIS_PREVIEW_ROWS = 4;
export const TETRIS_PREVIEW_BLOCK = 34;

/** Symmetric scale factor for side cards (stats + next piece). */
export const TETRIS_CARD_PADDING = 44;

/**
 * Stackfall palette — our own muted "mineral" tones (not the classic
 * cyan/yellow/purple/green/red/blue/orange per-piece mapping). Each
 * piece kind gets a tone from this palette; tiles are drawn with a
 * rounded glossy bevel, so colour is only one part of the look.
 */
export const STACKFALL_PALETTE = {
    jade: '#3FA796',
    amber: '#D49A3A',
    plum: '#9A5C8F',
    slate: '#5C7FA8',
    coral: '#D0705E',
    moss: '#7FA05A',
    sand: '#C9B48A',
};

/** Board / frame colours for the Stackfall look. */
export const STACKFALL_BOARD = {
    bgTop: '#14202B',
    bgBottom: '#0B1118',
    dot: 'rgba(160, 196, 210, 0.16)',
    ghost: '#BFE3DA',
    frame: '#3FA796',
    frameGlow: 'rgba(63, 167, 150, 0.35)',
    sweep: 'rgba(230, 248, 242, 0.85)',
    text: '#E8F4F0',
    textMuted: '#A9C2BC',
};

export const TETROMINOES = {
    I: { shape: [[1, 1, 1, 1]], color: STACKFALL_PALETTE.slate },
    O: { shape: [[1, 1], [1, 1]], color: STACKFALL_PALETTE.sand },
    T: { shape: [[0, 1, 0], [1, 1, 1]], color: STACKFALL_PALETTE.plum },
    S: { shape: [[0, 1, 1], [1, 1, 0]], color: STACKFALL_PALETTE.moss },
    Z: { shape: [[1, 1, 0], [0, 1, 1]], color: STACKFALL_PALETTE.coral },
    J: { shape: [[1, 0, 0], [1, 1, 1]], color: STACKFALL_PALETTE.jade },
    L: { shape: [[0, 0, 1], [1, 1, 1]], color: STACKFALL_PALETTE.amber },
};

export const TETRIS_PIECE_TYPES = Object.keys(TETROMINOES);

export function tetrisPlayfieldSx(accent) {
    void accent;
    return {
        boxSizing: 'border-box',
        p: 1,
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ARCADE_THEME.bg,
        borderRadius: 2,
        border: `1px solid ${ARCADE_THEME.border}`,
        boxShadow: `0 0 16px rgba(0,0,0,0.35)`,
        overflow: 'hidden',
    };
}

/** Gap between buttons and game row; canvas keeps its own side clearance. */
export const TETRIS_PLAY_GAP = 2.5;

/** Extra space between side cards and the canvas frame. */
export const TETRIS_CANVAS_SIDE_CLEARANCE = { xs: 2, md: 3, lg: 4 };

/** Layout for buttons + game row — no extra background (shell provides dark surface). */
export function tetrisPlayfieldContentSx() {
    return {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: TETRIS_PLAY_GAP,
        flex: 1,
        minHeight: 0,
        width: '100%',
        px: { xs: 3, md: 4, lg: 5 },
        py: { xs: 2.5, md: 3 },
    };
}

/** Three-zone row: stats centered left of canvas, next piece centered right. */
export function tetrisGameRowSx() {
    return {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        width: '100%',
        flex: 1,
        minHeight: 0,
    };
}

/** Centers a side card in the band between canvas and the outer play edge. */
export function tetrisSideSlotSx(side = 'left') {
    const towardCanvas = side === 'left'
        ? { pr: TETRIS_CANVAS_SIDE_CLEARANCE }
        : { pl: TETRIS_CANVAS_SIDE_CLEARANCE };
    return {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 0,
        minHeight: { xs: 'auto', md: 0 },
        ...towardCanvas,
    };
}

export function tetrisCanvasSlotSx() {
    return {
        flexShrink: 0,
        maxWidth: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
}

export function tetrisSideCardSize() {
    return TETRIS_PREVIEW_COLS * TETRIS_PREVIEW_BLOCK + TETRIS_CARD_PADDING;
}

export function tetrisSideCardSx() {
    const cardSize = tetrisSideCardSize();
    return {
        ...arcadeSidePanelSx(),
        flexShrink: 0,
        width: cardSize,
        maxWidth: '100%',
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        p: 2.25,
    };
}

/** @deprecated use tetrisPlayfieldContentSx */
export function tetrisPlayfieldRowSx(accent) {
    return tetrisPlayfieldContentSx();
}

export function tetrisNextPieceCardSx() {
    const base = tetrisSideCardSx();
    return {
        ...base,
        minHeight: base.width,
        justifyContent: 'flex-start',
    };
}

/** Outer canvas frame — first border + accent glow. */
export function tetrisCanvasOuterSx(accent) {
    const c = accent || STACKFALL_BOARD.frame;
    return {
        flexShrink: 0,
        boxSizing: 'border-box',
        maxWidth: '100%',
        p: { xs: 1, md: 1.25 },
        borderRadius: 2,
        border: `2px solid ${c}`,
        boxShadow: `0 0 24px ${STACKFALL_BOARD.frameGlow}, 0 0 6px ${STACKFALL_BOARD.frameGlow}`,
        background: 'linear-gradient(160deg, #1A2A33 0%, #0D151C 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
}

/** Inner canvas frame — second border tucked inside the glow. */
export function tetrisCanvasInnerSx(accent) {
    const c = accent || STACKFALL_BOARD.frame;
    return {
        boxSizing: 'border-box',
        maxWidth: '100%',
        p: 0.5,
        borderRadius: 1,
        border: `1px solid ${c}`,
        boxShadow: `inset 0 0 12px ${STACKFALL_BOARD.frameGlow}`,
        lineHeight: 0,
    };
}

export function tetrisCanvasStyle() {
    return {
        boxSizing: 'border-box',
        border: `1px solid ${ARCADE_THEME.grid}`,
        borderRadius: 4,
        display: 'block',
        width: 'auto',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        backgroundColor: STACKFALL_BOARD.bgBottom,
        aspectRatio: `${TETRIS_BOARD_WIDTH} / ${TETRIS_BOARD_HEIGHT}`,
    };
}

/** @deprecated use tetrisCanvasStyle */
export const TETRIS_CANVAS_STYLE = tetrisCanvasStyle();

export function arcadeSidePanelSx() {
    return {
        p: 2,
        bgcolor: ARCADE_THEME.cardBg,
        borderRadius: 3,
        border: `1px solid ${ARCADE_THEME.border}`,
    };
}

function hexToRgb(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Lighten (amt > 0) or darken (amt < 0) a hex colour; non-hex passes through. */
export function shadeColor(hex, amt) {
    const c = hexToRgb(hex);
    if (!c) return hex;
    const f = (v) => Math.max(0, Math.min(255, Math.round(amt >= 0 ? v + (255 - v) * amt : v * (1 + amt))));
    return `rgb(${f(c.r)}, ${f(c.g)}, ${f(c.b)})`;
}

function roundRectPath(ctx, x, y, w, h, r) {
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(x, y, w, h, rr);
        return;
    }
    if (typeof ctx.moveTo !== 'function' || typeof ctx.arcTo !== 'function') {
        if (typeof ctx.rect === 'function') ctx.rect(x, y, w, h);
        return;
    }
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    if (typeof ctx.closePath === 'function') ctx.closePath();
}

/**
 * Stackfall tile: rounded body, inner bevel (light top-left, dark
 * bottom-right) and a soft gloss band. `scale`/`glow` drive the lock pulse.
 */
export function drawTile(ctx, px, py, size, color, { scale = 1, glow = 0, alpha = 1 } = {}) {
    const inset = Math.max(1, Math.round(size * 0.06));
    const s = (size - inset * 2) * scale;
    const x = px + (size - s) / 2;
    const y = py + (size - s) / 2;
    const r = s * 0.22;
    const prevAlpha = ctx.globalAlpha;
    if (alpha !== 1) ctx.globalAlpha = alpha;

    // body
    ctx.fillStyle = color;
    ctx.fillRect(x, y, s, s); // solid base (also keeps simple canvas mocks happy)
    ctx.fillStyle = shadeColor(color, -0.28);
    roundRectPath(ctx, x, y, s, s, r);
    if (typeof ctx.fill === 'function') ctx.fill();
    ctx.fillStyle = color;
    const b = Math.max(2, s * 0.12);
    roundRectPath(ctx, x + b * 0.5, y + b * 0.5, s - b, s - b, r * 0.8);
    if (typeof ctx.fill === 'function') ctx.fill();

    // bevel highlight (top-left) and shade (bottom-right)
    if (typeof ctx.stroke === 'function') {
        ctx.lineWidth = Math.max(1, s * 0.06);
        ctx.strokeStyle = shadeColor(color, 0.35);
        ctx.beginPath();
        if (typeof ctx.moveTo === 'function') {
            ctx.moveTo(x + r, y + b * 0.6);
            ctx.lineTo(x + s - r, y + b * 0.6);
            ctx.moveTo(x + b * 0.6, y + r);
            ctx.lineTo(x + b * 0.6, y + s - r);
        }
        ctx.stroke();
    }

    // gloss band
    ctx.fillStyle = 'rgba(255, 255, 255, 0.16)';
    roundRectPath(ctx, x + s * 0.18, y + s * 0.14, s * 0.5, s * 0.18, s * 0.09);
    if (typeof ctx.fill === 'function') ctx.fill();

    if (glow > 0) {
        ctx.fillStyle = `rgba(255, 255, 255, ${Math.min(0.55, glow * 0.55).toFixed(3)})`;
        roundRectPath(ctx, x, y, s, s, r);
        if (typeof ctx.fill === 'function') ctx.fill();
    }
    ctx.globalAlpha = prevAlpha;
}

function drawCell(ctx, x, y, color, cellWidth, cellHeight = cellWidth, opts) {
    drawTile(ctx, x * cellWidth, y * cellHeight, Math.min(cellWidth, cellHeight), color, opts);
}

/**
 * Ghost piece: dotted outline + faint diagonal hatch in the board accent,
 * deliberately NOT a translucent copy of the piece.
 */
export function drawGhostCell(ctx, x, y, cellWidth, cellHeight = cellWidth, color = STACKFALL_BOARD.ghost) {
    const px = x * cellWidth + 3;
    const py = y * cellHeight + 3;
    const w = cellWidth - 6;
    const h = cellHeight - 6;
    if (typeof ctx.save === 'function') ctx.save();
    if (typeof ctx.setLineDash === 'function') ctx.setLineDash([3, 3]);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    if (typeof ctx.strokeRect === 'function') ctx.strokeRect(px, py, w, h);
    if (typeof ctx.setLineDash === 'function') ctx.setLineDash([]);
    ctx.globalAlpha = 0.28;
    ctx.lineWidth = 1;
    if (typeof ctx.moveTo === 'function') {
        ctx.beginPath();
        for (let d = 6; d < w + h; d += 7) {
            ctx.moveTo(px + Math.max(0, d - h), py + Math.min(h, d));
            ctx.lineTo(px + Math.min(w, d), py + Math.max(0, d - w));
        }
        ctx.stroke();
    }
    ctx.globalAlpha = 1;
    if (typeof ctx.restore === 'function') ctx.restore();
}

/**
 * Where would `piece` land? Cells occupied by the piece itself (if the GM
 * already merged it into `board`) are ignored. Pure helper; no gameplay effect.
 */
export function computeGhostY(board, piece, boardHeight = TETRIS_BOARD_HEIGHT) {
    if (!piece?.shape?.length) return null;
    const own = new Set();
    piece.shape.forEach((row, dy) => row.forEach((v, dx) => { if (v) own.add(`${piece.x + dx},${piece.y + dy}`); }));
    const fits = (oy) => piece.shape.every((row, dy) => row.every((v, dx) => {
        if (!v) return true;
        const bx = piece.x + dx;
        const by = oy + dy;
        if (by >= boardHeight) return false;
        if (by < 0) return true;
        if (own.has(`${bx},${by}`)) return true;
        return !board?.[by]?.[bx];
    }));
    let y = piece.y;
    while (fits(y + 1)) y += 1;
    return y;
}

function drawBoardBackground(ctx, { boardWidth, boardHeight, cellWidth, cellHeight, canvasWidth, canvasHeight }) {
    let bg = STACKFALL_BOARD.bgBottom;
    if (typeof ctx.createLinearGradient === 'function') {
        const g = ctx.createLinearGradient(0, 0, 0, canvasHeight);
        if (g && typeof g.addColorStop === 'function') {
            g.addColorStop(0, STACKFALL_BOARD.bgTop);
            g.addColorStop(1, STACKFALL_BOARD.bgBottom);
            bg = g;
        }
    }
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    // dot lattice at cell corners instead of ruled grid lines
    ctx.fillStyle = STACKFALL_BOARD.dot;
    for (let y = 1; y < boardHeight; y++) {
        for (let x = 1; x < boardWidth; x++) {
            ctx.fillRect(x * cellWidth - 1, y * cellHeight - 1, 2, 2);
        }
    }
}

/**
 * Line-clear sweep: a bright band crossing the given rows left→right with
 * the band's trail dissolving. `progress` 0..1.
 */
export function drawClearSweep(ctx, { rows = [], progress = 0, cellHeight = TETRIS_CELL_HEIGHT, canvasWidth = TETRIS_CANVAS_W }) {
    if (!rows.length || progress <= 0 || progress >= 1) return;
    const head = canvasWidth * progress;
    const prevAlpha = ctx.globalAlpha;
    rows.forEach((row) => {
        const y = row * cellHeight;
        ctx.globalAlpha = 0.35 * (1 - progress);
        ctx.fillStyle = STACKFALL_BOARD.sweep;
        ctx.fillRect(0, y + 2, head, cellHeight - 4);
        ctx.globalAlpha = 1 - progress * 0.5;
        ctx.fillRect(Math.max(0, head - 10), y, 10, cellHeight);
    });
    ctx.globalAlpha = prevAlpha;
}

export function drawTetrisBoard(ctx, {
    board,
    currentPiece,
    boardWidth = TETRIS_BOARD_WIDTH,
    boardHeight = TETRIS_BOARD_HEIGHT,
    cellWidth = TETRIS_CELL_WIDTH,
    cellHeight = TETRIS_CELL_HEIGHT,
    canvasWidth = TETRIS_CANVAS_W,
    canvasHeight = TETRIS_CANVAS_H,
    theme = ARCADE_THEME,
    showGhost = true,
    ghostPiece = null,
    effects = null,
}) {
    void theme;
    drawBoardBackground(ctx, { boardWidth, boardHeight, cellWidth, cellHeight, canvasWidth, canvasHeight });

    const pulse = effects?.pulse;
    const pulseSet = pulse?.cells?.length ? new Set(pulse.cells.map(([x, y]) => `${x},${y}`)) : null;
    const pulseT = pulse ? Math.max(0, Math.min(1, pulse.progress ?? 0)) : 1;
    const pulseOpts = pulseSet && pulseT < 1
        ? { scale: 1 + 0.08 * Math.sin(pulseT * Math.PI), glow: 1 - pulseT }
        : undefined;

    const gp = ghostPiece || currentPiece;
    if (showGhost && gp?.shape?.length) {
        const gy = computeGhostY(board, gp, boardHeight);
        if (gy != null && gy !== gp.y) {
            gp.shape.forEach((row, dy) => row.forEach((v, dx) => {
                if (v) drawGhostCell(ctx, gp.x + dx, gy + dy, cellWidth, cellHeight);
            }));
        }
    }

    for (let y = 0; y < boardHeight; y++) {
        for (let x = 0; x < boardWidth; x++) {
            if (board?.[y]?.[x]) {
                const opts = pulseOpts && pulseSet.has(`${x},${y}`) ? pulseOpts : undefined;
                drawCell(ctx, x, y, board[y][x], cellWidth, cellHeight, opts);
            }
        }
    }

    if (currentPiece) {
        for (let y = 0; y < currentPiece.shape.length; y++) {
            for (let x = 0; x < currentPiece.shape[y].length; x++) {
                if (currentPiece.shape[y][x]) {
                    drawCell(ctx, currentPiece.x + x, currentPiece.y + y, currentPiece.color, cellWidth, cellHeight);
                }
            }
        }
    }

    if (effects?.sweep) {
        drawClearSweep(ctx, { ...effects.sweep, cellHeight, canvasWidth });
    }
}

export function drawTetrisPauseOverlay(ctx, canvasWidth, canvasHeight, accent = ARCADE_THEME.gold) {
    ctx.fillStyle = 'rgba(8, 14, 20, 0.78)';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    ctx.fillStyle = accent;
    ctx.font = 'bold 48px "Trebuchet MS", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('PAUSED', canvasWidth / 2, canvasHeight / 2);

    ctx.font = 'bold 18px "Trebuchet MS", sans-serif';
    ctx.fillStyle = STACKFALL_BOARD.text;
    ctx.fillText('Press Esc or Space to continue', canvasWidth / 2, canvasHeight / 2 + 50);
}

/**
 * Draw the upcoming piece centered in a fixed preview grid.
 */
export function drawNextPiecePreview(ctx, piece, {
    cols = TETRIS_PREVIEW_COLS,
    rows = TETRIS_PREVIEW_ROWS,
    blockSize = TETRIS_PREVIEW_BLOCK,
    theme = ARCADE_THEME,
} = {}) {
    const width = cols * blockSize;
    const height = rows * blockSize;

    void theme;
    drawBoardBackground(ctx, { boardWidth: cols, boardHeight: rows, cellWidth: blockSize, cellHeight: blockSize, canvasWidth: width, canvasHeight: height });

    if (!piece?.shape?.length) return;

    const offsetX = Math.floor((cols - piece.shape[0].length) / 2);
    const offsetY = Math.floor((rows - piece.shape.length) / 2);

    for (let y = 0; y < piece.shape.length; y++) {
        for (let x = 0; x < piece.shape[y].length; x++) {
            if (piece.shape[y][x]) {
                drawCell(ctx, offsetX + x, offsetY + y, piece.color, blockSize);
            }
        }
    }
}
